import React, { useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import DynamicGroupWizard from "../DynamicGroupWizard/DynamicGroupWizard";
import {
  DynamicGroupWizardData,
  INITIAL_DYNAMIC_GROUP_DATA,
} from "../DynamicGroupWizard/types";
import { buildInitialEnrollConfig } from "../GroupWizard/types";
import { enrollGroupToLearningObjects } from "../enrollGroupToLearningObjects";

const EditDynamicGroup: React.FC = () => {
  const { id: corporateId = "", groupId = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("group-wizard-v2");
  const backTo = `/v2/corporate/${corporateId}/detail/users-groups?tab=dynamic-groups`;
  const [submitting, setSubmitting] = useState(false);

  const dynamicGroupsUrl = ServicesURL.dynamicGroups.replace(
    ":idCorporate",
    corporateId,
  );

  const {
    dataDetail,
    isLoading,
    mutate: mutateDetail,
  } = useFetchDetail(
    dynamicGroupsUrl,
    groupId,
    PagesID["corporate.groups-dynamic.edit"],
  );

  const { trigger } = useSWRMutation(
    `${dynamicGroupsUrl}/${groupId}`,
    (url: string, { arg }: { arg: any }) => fetcher(url, MethodType.PUT, arg),
  );

  const initialData = useMemo<DynamicGroupWizardData | null>(
    () =>
      !isLoading && dataDetail && !Array.isArray(dataDetail)
        ? {
            name: dataDetail.name || "",
            description: dataDetail.description || "",
            sets:
              Array.isArray(dataDetail.sets) && dataDetail.sets.length > 0
                ? dataDetail.sets
                : INITIAL_DYNAMIC_GROUP_DATA.sets,
            loSelections: [],
            enrollConfig: buildInitialEnrollConfig(),
          }
        : null,
    [dataDetail, isLoading],
  );

  const onComplete = async (data: DynamicGroupWizardData) => {
    setSubmitting(true);
    try {
      await trigger({
        name: data.name || "",
        description: data.description || "",
        sets: data.sets,
      });
      await enrollGroupToLearningObjects({
        corporateId,
        groupId,
        groupType: "DYNAMIC",
        loSelections: data.loSelections,
        enrollType: data.enrollConfig.enrollType || "TOP",
        config: data.enrollConfig,
      });
      navigate(backTo);
      mutateDetail(undefined, { revalidate: false });
    } catch (error) {
      setSubmitting(false);
      throw error;
    }
  };

  if (!initialData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DynamicGroupWizard
      initialData={initialData}
      backTo={backTo}
      onComplete={onComplete}
      isCompleting={submitting}
      title={t("titleEditDynamic")}
      abandonMode="edit"
    />
  );
};

export default EditDynamicGroup;
